'use client';

import { useEffect, useState, useCallback } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { wholesaleApi, CrmAlert } from '@/lib/api';
import { Bell, TrendingDown, Clock, AlertTriangle, UserPlus, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import Link from 'next/link';

const alertIcons: Record<string, typeof Bell> = {
  health_declining: TrendingDown,
  reorder_overdue: Clock,
  reorder_reminder: Bell,
  order_anomaly: AlertTriangle,
  new_buyer: UserPlus,
};

const alertColors: Record<string, string> = {
  health_declining: 'text-red-500',
  reorder_overdue: 'text-orange-500',
  reorder_reminder: 'text-blue-500',
  order_anomaly: 'text-yellow-500',
  new_buyer: 'text-green-500',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function NotificationCenter() {
  const [alerts, setAlerts] = useState<CrmAlert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchAlerts = useCallback(() => {
    wholesaleApi.getCrmAlerts({ take: 10, status: 'pending' })
      .then((result) => {
        setAlerts(Array.isArray(result?.data) ? result.data : []);
        setUnreadCount(typeof result?.unreadCount === 'number' ? result.unreadCount : 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchAlerts();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleMarkAllRead = async () => {
    try {
      await wholesaleApi.markAllAlertsRead();
      fetchAlerts();
    } catch {
      // Failed silently — user can retry
    }
  };

  const handleDismiss = async (id: string) => {
    try {
      await wholesaleApi.dismissAlert(id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Failed silently — user can retry
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleMarkAllRead}>
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4 text-center">No new notifications</p>
          ) : (
            <div className="divide-y">
              {alerts.map((alert) => {
                const Icon = alertIcons[alert.alertType] ?? Bell;
                const color = alertColors[alert.alertType] ?? 'text-gray-500';
                return (
                  <div key={alert.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50">
                    <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', color)} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-tight">{alert.title}</p>
                      {alert.body && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{alert.body}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{timeAgo(alert.createdAt)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => handleDismiss(alert.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t px-4 py-2">
          <Link href="/sell/insights" onClick={() => setOpen(false)}>
            <Button variant="ghost" size="sm" className="w-full text-xs">View all insights</Button>
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
